import Icon from "@Components/generic/icon";
import { useActivityContext } from "@Context/game/activity/activity-context";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import ActivitySessionRow from "@Components/game/activity/activity-session-row";
import { ActivityRows, SectionHeaderButton } from "@Components/game/activity/styled-components";
import { type IUserSessionGroup } from "@Components/game/activity/utils";
import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import { Box, Collapse, Stack, Typography, useTheme } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";

interface IActivityMonthGroup {
  group: IUserSessionGroup;
}

const ActivityMonthGroup = ({ group }: IActivityMonthGroup) => {
  const theme = useTheme();
  const prefersReducedMotion = useReducedMotion();
  const { expandedMonths, toggleMonth } = useActivityContext();
  const isExpanded = expandedMonths[group.month];

  return (
    <Stack gap={2} data-activity-month={group.month}>
      <HapticsButtonWrapper>
        <SectionHeaderButton type="button" onClick={() => toggleMonth(group.month)} aria-expanded={isExpanded}>
          <Typography variant="textLg700" color="common.white">
            {group.month}
          </Typography>

          <Box
            sx={{
              width: theme.spacing(4),
              height: theme.spacing(4),
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1px solid ${theme.palette.surfaceOverlay.border}`,
              backgroundColor: theme.palette.surfaceOverlay.background,
            }}
          >
            <Icon icon={isExpanded ? faChevronUp : faChevronDown} sx={{ color: theme.palette.text.secondary, fontSize: theme.spacing(1.75) }} />
          </Box>
        </SectionHeaderButton>
      </HapticsButtonWrapper>

      <Collapse in={isExpanded} timeout={400}>
        <ActivityRows data-activity-session-rows>
          {group.items.map((session, index) => (
            <motion.div
              key={session.play_session_id}
              initial="collapsed"
              animate={isExpanded ? "expanded" : "collapsed"}
              variants={{
                collapsed: {
                  opacity: 0,
                  y: prefersReducedMotion ? 0 : 6,
                  transition: { duration: prefersReducedMotion ? 0 : 0.16 },
                },
                expanded: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: prefersReducedMotion ? 0 : 0.28,
                    delay: prefersReducedMotion ? 0 : Math.min(index * 0.025, 0.2),
                    ease: [0.22, 1, 0.36, 1],
                  },
                },
              }}
            >
              <ActivitySessionRow session={session} />
            </motion.div>
          ))}
        </ActivityRows>
      </Collapse>
    </Stack>
  );
};

export default ActivityMonthGroup;
